/**
 * Login Page
 *
 * Professional B2B-optimized login page that redirects to Cognito Hosted UI.
 *
 * Key Features:
 * - Full-screen centered layout with gradient background
 * - Mobile-optimized with large touch targets
 * - Single "Continue with Email" button (doesn't pre-declare login vs signup)
 * - Cognito Hosted UI automatically detects existing account and shows appropriate form
 * - Professional appearance that signals enterprise-grade SaaS
 *
 * UX Rationale (from Inara):
 * - Doug needs professional B2B credibility
 * - Debra needs mobile-focused simplicity
 * - Sarah evaluates security carefully
 * - 88% of users won't return after bad UX - login cards feel unprofessional for B2B
 */

import { useState, useEffect } from 'react'
import { Card, Button, Typography, Space, Alert } from 'antd'
import { RocketOutlined } from '@ant-design/icons'
import { authService } from '@/services/authService'

const { Title, Text } = Typography

export const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset loading state when component mounts (e.g., user hits back button)
  useEffect(() => {
    setIsLoading(false)
    setError(null)
  }, [])

  // Fire attribution tracking for direct referrals (e.g., Square marketplace)
  // This sets rp_visitor_id cookie BEFORE OAuth redirect so callback can link attribution
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const hasTrackingParams = ['utm_source', 'utm_medium', 'utm_campaign', 'ref', 'qr'].some(
      (p) => params.has(p)
    )

    if (hasTrackingParams) {
      // Environment-aware tracking URL
      const host = window.location.hostname
      const apiBase =
        host === 'app.rocketpricing.com'
          ? 'https://api.rocketpricing.com/attr'
          : 'https://api-dev.rocketpricing.com/dev/attr'

      const trackingUrl = new URL(apiBase)
      ;['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((param) => {
        const value = params.get(param)
        if (value) trackingUrl.searchParams.set(param, value)
      })

      const qrCode = params.get('qr')
      if (qrCode) trackingUrl.searchParams.set('qr', qrCode)

      const ref = params.get('ref')
      if (ref) trackingUrl.searchParams.set('ref', ref)

      trackingUrl.searchParams.set('lp', window.location.href)

      // Fire tracking pixel - cookie will be set by response
      const img = new Image()
      img.src = trackingUrl.toString()
    }
  }, [])

  const handleLogin = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await authService.signInWithHostedUI()
      // Cognito redirect happens automatically - no need to reset loading state
    } catch (err) {
      setIsLoading(false)
      setError(
        'Unable to connect to login service. Please check your internet connection and try again.'
      )
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--rp-gradient-background)',
        padding: '24px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Logo and branding */}
          <RocketOutlined
            style={{
              fontSize: 64,
              color: '#1890ff',
              marginTop: 16,
            }}
          />

          {/* Heading */}
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              Welcome to Rocket Pricing
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Sign in to manage your pricing and costs
            </Text>
          </div>

          {/* Subscription required warning - shown when redirected due to no plan */}
          {new URLSearchParams(window.location.search).get('no_plan') === 'true' && (
            <Alert
              message="Subscription Required"
              description="Your account does not have an active subscription plan. Please contact Rocket Pricing to activate your account."
              type="warning"
              showIcon
              closable
              onClose={() => {
                window.history.replaceState({}, document.title, window.location.pathname)
              }}
            />
          )}

          {/* No organization access - shown when user has no orgs */}
          {new URLSearchParams(window.location.search).get('no_org') === 'true' && (
            <Alert
              message="No Organization Access"
              description="Your account isn't connected to any organization yet. Please contact your administrator or reach out to Rocket Pricing support at support@rocketpricing.com to get set up."
              type="warning"
              showIcon
              closable
              onClose={() => {
                window.history.replaceState({}, document.title, window.location.pathname)
              }}
            />
          )}

          {/* Error message */}
          {error && (
            <Alert
              message="Login Error"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          {/* Primary action button */}
          <Button
            type="primary"
            size="large"
            block
            onClick={handleLogin}
            loading={isLoading}
            disabled={isLoading}
            style={{
              minHeight: 48,
              fontSize: 16,
              marginTop: 8,
            }}
          >
            {isLoading ? 'Redirecting...' : 'Continue with Email'}
          </Button>

          {/* Helper text */}
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            New user? No problem - we'll guide you through signup.
          </Text>
        </Space>
      </Card>
    </div>
  )
}

export default LoginPage
